import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../../@types/redux';
import { dataSetPackLines, dataSetSites } from '../../../store/masterData/Actions';
import { formatDateTime, isNullOrWhiteSpace, upsertArrayElement } from '../../../services/appFunctionsService';
import { generalShowErrorSnackbar, generalShowSuccessSnackbar } from '../../../store/general/Functions';
import Screen from '../../../components/Screen';
import PackmanDialog from '../../../components/dialog/PackmanDialog';
import { IPackLine, PackLine } from '../../../@types/model/masterData/packLine/packLine';
import { PackLineFormValues, IPackLineFormValues } from '../../../@types/model/masterData/packLine/packLineFormValues';
import PackLineForm from './form/PackLineForm';
import { useLocation } from 'react-router';
import { ConfirmationDialog, CustomTable } from '@zz2/zz2-ui';
import { v4 as uuidv4 } from 'uuid';
import { IApiError } from '../../../@types/other';
import PackLineHttpService from '../../../services/http/masterData/packLineHttpService';
import SiteHttpService from '../../../services/http/masterData/siteHttpService';
import { setPackLineMasterDataIndexedDB, syncMasterData } from '../../../services/masterDataSyncService';

const PackLineScreen = () : React.ReactElement => {
    const dispatch = useAppDispatch();
    const packLines = useAppSelector(x => x.masterData.packLines);
    const sites = useAppSelector(x => x.masterData.sites);
    const auth = useAppSelector(x => x.auth);
    const pathname = useLocation().pathname;

    const [isLoading, setIsLoading] = useState(false);
    const [selectedPackLine, setSelectedPackLine] = useState<IPackLine>();
    const [formGuid, setFormGuid] = useState<string>();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
    const [deletingPackLine, setDeletingPackLine] = useState<IPackLine>();

    useEffect(() => {
        const fetchData = async () => {
            const isIndexedDBAvailable = !!self.indexedDB;
            if (isIndexedDBAvailable) {
                await syncMasterData(false);
            } else {
                setIsLoading(true);
                try {
                    const res = await PackLineHttpService.getPackLineData();
                    const res2 = await SiteHttpService.getSiteData();
                    dispatch(dataSetSites(res2.data));
                    dispatch(dataSetPackLines(res.data));
                } catch (error : unknown) {
                    const apiError = error as IApiError;
                    generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred while loading pack lines.');
                } finally {
                    setIsLoading(false);
                }
            }
        };
        fetchData();
    }, [dispatch]);

    const openDialog = useCallback(() => {
        setFormGuid(uuidv4());
        setIsDialogOpen(true);
    }, []);

    const closeDialog = useCallback(() => {
        setIsDialogOpen(false);
        setSelectedPackLine(undefined);
        setFormGuid(undefined);
    }, []);

    const editPackLine = useCallback((packLine : IPackLine) => {
        setSelectedPackLine(packLine);
        setFormGuid(packLine.guid);
        setIsDialogOpen(true);
    }, []);

    const onSubmit = async (values : IPackLineFormValues) => {
        setIsLoading(true);
        let payload = { ...values };
        if (isNullOrWhiteSpace(payload.guid)) {
            payload.guid = formGuid ?? '';
        }

        try {
            const res = await PackLineHttpService.addOrUpdatePackLine(PackLine(payload));
            const updatedList = upsertArrayElement(packLines, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetPackLines(updatedList));
            await setPackLineMasterDataIndexedDB(updatedList);

            generalShowSuccessSnackbar(selectedPackLine ? 'Pack line updated successfully.' : 'Pack line added successfully.');
            closeDialog();
        } catch (error : unknown) {
            const apiError = error as IApiError;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred updating pack line data.');
        } finally {
            setIsLoading(false);
        }
    };

    const getSiteShortDescription = useCallback((siteId : number) => {
        const site = sites?.find(x => x.id === siteId);
        return site ? site.shortDescription : '';
    }, [sites]);

    const openDeleteConfirmationPopup = useCallback((packLine : IPackLine) => {
        setDeletingPackLine(packLine);
        setIsDeletePopupOpen(true);
    }, []);

    const closeDeleteConfirmationPopup = useCallback(() => {
        setDeletingPackLine(undefined);
        setIsDeletePopupOpen(false);
    }, []);

    const removePackLine = async () => {
        if (!deletingPackLine) return;
        const updatedPackLine = { ...deletingPackLine, isActive: false };
        setIsLoading(true);
        try {
            const res = await PackLineHttpService.deletePackLine(updatedPackLine.id);
            const updatedList = upsertArrayElement(packLines, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetPackLines(updatedList));
            await setPackLineMasterDataIndexedDB(updatedList);
            generalShowSuccessSnackbar('Pack line deleted successfully.');
        } catch (error : unknown) {
            const apiError = error as IApiError;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred deleting pack line.');
        } finally {
            closeDeleteConfirmationPopup();
            setIsLoading(false);
        }
    };

    const initialValues = useMemo(() => {
        if (isDialogOpen) {
            return PackLineFormValues(selectedPackLine, sites);
        }
    }, [selectedPackLine, sites, isDialogOpen]);

    const hasEditingRight = useMemo(() => {
        const rights = auth?.session?.user?.rights || [];
        return rights.some(x => pathname.includes(x.url) && x.isActive && x.code.endsWith('_EDIT'));
    }, [auth, pathname]);

    return (
        <Screen isLoading={isLoading}>
            {isDialogOpen && initialValues &&
                <PackmanDialog
                    title='Pack Line'
                    isEdit={!!selectedPackLine}
                    isLoading={isLoading}
                    isOpen={isDialogOpen}
                    onClose={closeDialog}
                >
                    <PackLineForm
                        isLoading={isLoading}
                        initialValues={initialValues}
                        onSubmit={onSubmit}
                        onClose={closeDialog}
                    />
                </PackmanDialog>
            }
            <div className='fdc hfill'>
                <Card className='fdc hfill'>
                    <CustomTable<IPackLine>
                        enableAdding={hasEditingRight}
                        addFunction={openDialog}
                        editFunction={editPackLine}
                        enableEditing={hasEditingRight}
                        enableDeleting={packLine => packLine.isActive && hasEditingRight}
                        deleteFunction={openDeleteConfirmationPopup}
                        enableSorting
                        enableFiltering
                        fitWidthToPage
                        enablePagination
                        columns={[
                            { title: 'Code', field: 'code', enableFiltering: true, enableSorting: true },
                            { title: 'Name', field: 'name', enableFiltering: true, enableSorting: true },
                            { title: 'Site', field: 'siteId', formatFunction: getSiteShortDescription, enableFiltering: true, enableSorting: true },
                            { title: 'Created By', field: 'createdByName', enableFiltering: true, enableSorting: true },
                            { title: 'Created On', field: 'createdOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Updated By', field: 'updatedByName', enableFiltering: true, enableSorting: true },
                            { title: 'Updated On', field: 'updatedOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Active?', field: 'isActive', type: 'boolean', enableFiltering: true, enableSorting: true },
                        ]}
                        rows={packLines}
                        initialSortOrder={[{ columnName: 'id_Id', direction: 'asc' }]}
                        pageSizes={[50, 150, 250, 500, 1000]}
                        pageHeight={190}
                        isActive={row => row.isActive}
                    />
                </Card>
            </div>
            <ConfirmationDialog
                title='Delete Pack Line'
                open={isDeletePopupOpen}
                description='Are you sure you want to delete this pack line?'
                onAccept={removePackLine}
                onClose={closeDeleteConfirmationPopup}
                dialogType='orange'
                isLoading={isLoading}
            />
        </Screen>
    );
};

export default PackLineScreen;
